import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cn } from "@repo/ui/lib/utils";
import { ComponentPropsWithoutRef, ElementRef, forwardRef } from "react";

type ProfileAvatarProps = {
  name: string;
  email: string;
  image: string;
  notificationsCounter?: number;
};

const Avatar = forwardRef<
  ElementRef<typeof AvatarPrimitive.Root>,
  ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      "relative flex h-[46px] w-[46px] max-md:h-[24px] max-md:w-[24px] shrink-0 overflow-hidden rounded-full",
      className,
    )}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = forwardRef<
  ElementRef<typeof AvatarPrimitive.Image>,
  ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn(
      "aspect-square h-[46px] w-[46px] max-md:h-[24px] max-md:w-[24px]",
      className,
    )}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = forwardRef<
  ElementRef<typeof AvatarPrimitive.Fallback>,
  ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-[46px] w-[46px] max-md:h-[24px] max-md:w-[24px] items-center justify-center rounded-full bg-primary-magenta text-[18px] max-md:text-[16px] text-primary-white",
      className,
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

const ProfileAvatar: React.FC<ProfileAvatarProps> = ({
  name,
  email,
  image,
  notificationsCounter = 0,
}) => (
  <div className="flex gap-[20px]">
    <div className="text-right max-lg:hidden">
      <div className="text-[20px] font-bold text-primary-magenta dark:text-primary-white">
        {name}
      </div>
      <p className="text-[16px] font-normal text-primary-black dark:text-primary-white">
        {email}
      </p>
    </div>
    <div className="relative">
      {!!notificationsCounter && (
        <div className="hidden max-md:block absolute rounded-full pb-2 w-4 h-4 text-[10px] font-bold bg-primary-black top-[-7px] right-[-7px] z-10 leading-4">
          {notificationsCounter}
        </div>
      )}
      <Avatar>
        <AvatarImage src={image} />
        <AvatarFallback>{name?.charAt(0)}</AvatarFallback>
      </Avatar>
    </div>
  </div>
);

export { ProfileAvatar, type ProfileAvatarProps };
